import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Highcharts from 'highcharts';
import { HighchartsChartComponent, providePartialHighcharts } from 'highcharts-angular';
import { debounceTime, distinctUntilChanged, filter, finalize, fromEvent, switchMap, tap } from 'rxjs';

import { FieldTypeEnum, RestrictionType } from '@models/module/fields/enums';
import { GridSearchEntityInstance, SearchViewModel } from '@models/module/grid';
import { DataListFields, GridFieldsModel } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { ScreenModel, ScreenTypeEnum } from '@module-definition/screen/common';
import {
  GANTT_DATE_FIELDS_TYPE_MISMATCH_ERROR,
  GanttFormatterPoint,
  GanttPointData,
  GanttService,
  isCompleteDateRange,
  toValidTime,
} from '@module-instance/gantt/common';
import { TableDataService } from '@shared/components/data-list/common/services/table-data.service';
import { ItvDateTimeWithRestrictionsPipe } from '@shared/pipes/itv-date.pipe';
import { ItvNumberWithFormattingsPipe } from '@shared/pipes/itv-number.pipe';
import { UserStore } from '@signal-store/user.store';
import { NotFoundComponent } from '@system/not-found/not-found.component';

const GANTT_GRID_WIDTH_RATIO = 0.5;
const GANTT_COLUMN_PADDING_PX = 31;
const GANTT_MAX_PIXELS_PER_WEIGHT = 100;
const GANTT_MIN_BAR_LABEL_WIDTH_PX = 30;
const GANTT_LAYOUT_SETTLE_MS = 300;
const GANTT_MOBILE_CHART_WIDTH_PX = 900;
const GANTT_DEFAULT_CONTAINER_WIDTH_PX = 800;
const GANTT_DEFAULT_CONTAINER_HEIGHT_PX = 600;
const GANTT_MIN_CHART_HEIGHT_PX = 200;
const GANTT_DEPENDENCY_BANNER_HEIGHT_PX = 28;
const GANTT_ROW_HEIGHT_PX = 40;
const GANTT_EXTREMES_MIN_DELTA = 0.01;
const GANTT_EXTREMES_MAX_DELTA = 0.05;

type GanttScrollAxis = Highcharts.Axis & { len?: number; staticScale?: number };

/**
 * Treegrid collapse keeps row height only when axis.staticScale is set, but
 * options.staticScale together with chart.height triggers adjustHeight resizing.
 * Set the axis property only so scrollMode works without changing chart height.
 */
function applyGanttYAxisCollapseScrollMode(chart: Highcharts.Chart): void {
  const yAxis = chart.yAxis[0] as GanttScrollAxis | undefined;
  if (yAxis) {
    yAxis.staticScale = GANTT_ROW_HEIGHT_PX;
  }
}

function ganttFitHeight(axis: GanttScrollAxis): number {
  const rowHeight = axis.staticScale || GANTT_ROW_HEIGHT_PX;
  const usable = (axis.len ?? 0) - rowHeight;
  return Math.max(rowHeight, Math.floor(usable / rowHeight) * rowHeight);
}

function ganttFitMax(axis: GanttScrollAxis, fromMin: number): number {
  const dataMax = axis.getExtremes().dataMax ?? axis.max ?? 0;
  const fitMax = Math.round(axis.toValue(axis.toPixels(fromMin) + ganttFitHeight(axis)));
  return Math.min(dataMax, fitMax);
}

function ganttMaxScrollMin(axis: GanttScrollAxis): number {
  const ext = axis.getExtremes();
  const dataMin = ext.dataMin ?? 0;
  const dataMax = ext.dataMax ?? 0;
  const bottomMin = Math.round(axis.toValue(axis.toPixels(dataMax) - ganttFitHeight(axis)));
  return Math.max(dataMin, Math.min(bottomMin, dataMax));
}

function ganttDataFetchKey(params: {
  contractId: number;
  moduleId: number;
  search: SearchViewModel | null;
  dataReloadToken: number;
}): string {
  const { contractId, moduleId, search, dataReloadToken } = params;
  if (!search) {
    return `${contractId}-${moduleId}-${dataReloadToken}`;
  }
  return `${contractId}-${moduleId}-${dataReloadToken}-${search.page}-${search.pagesize}-${search.criteria ?? ''}-${search.isCurrent}-${search.showPresets}-${JSON.stringify(search.sort ?? [])}-${JSON.stringify(search.filter ?? [])}`;
}

@Component({
  selector: 'cipo-gantt-view',
  templateUrl: './gantt-view.component.html',
  styleUrl: './gantt-view.component.scss',
  imports: [
    HighchartsChartComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
    FlexLayoutModule,
    NotFoundComponent,
  ],
  providers: [
    providePartialHighcharts({
      modules: () => [import('highcharts/esm/modules/gantt')],
    }),
  ],
})
export class GanttViewComponent {
  private ganttService = inject(GanttService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private datePipe = inject(ItvDateTimeWithRestrictionsPipe);
  private numberPipe = inject(ItvNumberWithFormattingsPipe);
  private tableDataService = inject(TableDataService);
  private elementRef = inject(ElementRef);
  protected userStore = inject(UserStore);
  protected readonly ganttMobileChartWidthPx = GANTT_MOBILE_CHART_WIDTH_PX;

  private containerHeight = signal(GANTT_DEFAULT_CONTAINER_HEIGHT_PX);
  private containerWidth = signal(GANTT_DEFAULT_CONTAINER_WIDTH_PX);

  contractId = input<number>();
  moduleId = input<number>();
  search = input<SearchViewModel>(null);
  gridFields = input<GridFieldsModel>(null);
  dataReloadToken = input(0);
  errorMessage = model<string>();
  actions = input<DataListViewAction[]>([]);
  isLoading = model<boolean>();

  screenId = computed(() => this.gridFields()?.otherScreens.find(s => s.typeId === ScreenTypeEnum.Gantt)?.key ?? null);
  rows = signal<GridSearchEntityInstance[]>([]);
  screenConfig = signal<ScreenModel>(null);

  private dateFieldKeys = computed(() => this.filterFields(this.gridFields()?.fields ?? []));

  startFieldKey = computed(() => {
    const fields = this.gridFields()?.fields ?? [];
    const dateFieldId = this.screenConfig()?.dateFieldId;
    const configured = this.filterFields(fields, dateFieldId);
    if (dateFieldId != null) {
      return configured[0] ?? null;
    }
    return configured[0] ?? this.dateFieldKeys()[0] ?? null;
  });
  endFieldKey = computed(() => {
    const fields = this.gridFields()?.fields ?? [];
    const endDateFieldId = this.screenConfig()?.endDateFieldId;
    const configured = this.filterFields(fields, endDateFieldId);
    if (endDateFieldId != null) {
      return configured[0] ?? null;
    }
    return configured[0] ?? this.dateFieldKeys()[1] ?? null;
  });

  chartOptions = computed<Highcharts.Options>(() => this.buildChartOptions());

  private dateFieldsTypeMismatch = computed(() => {
    const config = this.screenConfig();
    const allFields = this.gridFields()?.fields ?? [];
    const startDateField = this.findDateField(allFields, config?.dateFieldId, this.startFieldKey());
    const endDateField = this.findDateField(allFields, config?.endDateFieldId, this.endFieldKey());

    if (!startDateField || !endDateField) {
      return false;
    }

    return (
      (startDateField.restrictions ?? []).find(r => r.key === RestrictionType.DateTime)?.value !==
      (endDateField.restrictions ?? []).find(r => r.key === RestrictionType.DateTime)?.value
    );
  });

  constructor() {
    effect(() => {
      this.errorMessage.set(this.dateFieldsTypeMismatch() ? GANTT_DATE_FIELDS_TYPE_MISMATCH_ERROR : null);
    });

    afterNextRender(() => {
      this.measureContainer();
      const timeoutId = setTimeout(() => this.measureContainer(), GANTT_LAYOUT_SETTLE_MS);
      this.destroyRef.onDestroy(() => clearTimeout(timeoutId));
    });

    // Re-measure after layout settles; skip transient 0-size reads that break Highcharts layout.
    fromEvent(window, 'resize')
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => requestAnimationFrame(() => this.measureContainer()));

    // Fetch when contract/module/search/token change; gridFields must be present (cleared during field reload).
    toObservable(
      computed(() => ({
        contractId: this.contractId(),
        moduleId: this.moduleId(),
        search: { ...this.search(), sort: null },
        dataReloadToken: this.dataReloadToken(),
        hasGridFields: !!this.gridFields(),
      })),
    )
      .pipe(
        filter(({ moduleId, search, hasGridFields }) => !!moduleId && !!search && hasGridFields),
        distinctUntilChanged((a, b) => ganttDataFetchKey(a) === ganttDataFetchKey(b)),
        switchMap(({ contractId, moduleId, search }) => {
          this.isLoading.set(true);
          return this.ganttService
            .getData(search, contractId, moduleId)
            .pipe(finalize(() => this.isLoading.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(rows => this.rows.set(rows));

    // Reload screen config when grid fields refresh so date field ids stay in sync.
    toObservable(
      computed(() => ({
        screenId: this.screenId(),
        gridFields: this.gridFields(),
      })),
    )
      .pipe(
        tap(({ gridFields }) => {
          if (!gridFields) {
            this.screenConfig.set(null);
          }
        }),
        filter(
          (params): params is { screenId: number; gridFields: GridFieldsModel } =>
            params.screenId != null && !!params.gridFields,
        ),
        distinctUntilChanged((a, b) => a.screenId === b.screenId && a.gridFields === b.gridFields),
        switchMap(({ screenId }) => this.ganttService.getScreen(screenId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(screen => {
        if (screen) this.screenConfig.set(screen);
      });
  }

  private buildChartOptions(): Highcharts.Options {
    const rows = this.rows();
    const startKey = this.startFieldKey();
    const endKey = this.endFieldKey();
    const config = this.screenConfig();
    const fields = this.gridFields()?.fields?.filter(f => !f.isFilter) ?? [];

    if (!startKey || !endKey) {
      return { series: [] };
    }

    const allFields = this.gridFields()?.fields ?? [];
    const startDateField = this.findDateField(allFields, config?.dateFieldId, startKey);
    const endDateField = this.findDateField(allFields, config?.endDateFieldId, endKey);

    if (!startDateField || !endDateField) {
      return { series: [] };
    }

    // Categorize rows by date availability
    const { completeRows, startOnlyRows, endOnlyRows, noDateRows } = this.ganttService.categorizeRowsByDates(
      rows,
      startKey,
      endKey,
    );

    const descriptionField =
      config?.descriptionFieldId != null ? allFields.find(f => f.id === config.descriptionFieldId) : null;
    const labelKey = descriptionField ? this.tableDataService.getFieldName(descriptionField) : null;
    const relKey = `parent_entity_instance_id`;
    const firstField = fields[0];
    const firstFieldKey = firstField ? this.tableDataService.getFieldName(firstField) : null;
    const datePipe = this.datePipe;
    const numberPipe = this.numberPipe;
    const currency = this.userStore.currency();

    const formatFirstField = (value: unknown): string => {
      if (value == null || value === '') return '';
      if (firstField?.typeId === FieldTypeEnum.Date) {
        return datePipe.transform(value as string, firstField.restrictions) ?? String(value);
      }
      if (firstField?.typeId === FieldTypeEnum.Number) {
        return (
          numberPipe.transform(value as number, firstField.formattings, firstField.restrictions, currency) ??
          String(value)
        );
      }
      return String(value);
    };

    // Flatten all rows together so parent/child relationships are preserved regardless of
    // whether a row has complete, partial, or missing dates.
    const sortByStart = this.ganttService.createSortByStartComparator(startKey);
    const sorted = this.ganttService.flattenTree(
      [...completeRows, ...startOnlyRows, ...endOnlyRows, ...noDateRows],
      relKey,
      sortByStart,
    );

    const allRowIds = new Set(sorted.map(r => String(r.entity_instance_id)));

    const now = Date.now();
    const oneDay = 24 * 3600 * 1000;

    const ganttData: GanttPointData[] = sorted.map(r => {
      const id = String(r.entity_instance_id);
      const parentRaw = r[relKey];
      const parentCandidate = parentRaw ? String(parentRaw) : undefined;
      const parent = parentCandidate && allRowIds.has(parentCandidate) ? parentCandidate : undefined;
      const name = firstFieldKey ? formatFirstField(r[firstFieldKey]) : (r.full_doc_num ?? id);

      const startTime = toValidTime(r[startKey]);
      const endTime = toValidTime(r[endKey]);
      const hasStart = startTime != null;
      const hasEnd = endTime != null;
      const isComplete = hasStart && hasEnd && isCompleteDateRange(startTime, endTime);

      if (isComplete) {
        // Complete rows — have both start and end
        return {
          id,
          name,
          start: startTime,
          end: endTime,
          color: (r.state_color as string) || null,
          ...(parent ? { parent, dependency: [parent] } : {}),
          custom: r,
        } as GanttPointData;
      }

      if (hasStart && !hasEnd) {
        // Start only — ongoing, dashed trailing bar
        return {
          id,
          name,
          start: startTime,
          end: Math.max(now, startTime + oneDay),
          color: (r.state_color as string) || null,
          className: 'gantt-ongoing',
          ...(parent ? { parent, dependency: [parent] } : {}),
          custom: r,
        } as GanttPointData;
      }

      if (!hasStart && hasEnd) {
        // End only — right-anchored marker
        return {
          id,
          name,
          start: Math.min(now, endTime) - oneDay,
          end: endTime,
          color: (r.state_color as string) || '#9E9E9E',
          className: 'gantt-end-only',
          ...(parent ? { parent, dependency: [parent] } : {}),
          custom: r,
        } as GanttPointData;
      }

      // No dates — not yet planned, rendered as transparent milestone
      return {
        id,
        name,
        start: now,
        end: now,
        milestone: true,
        color: 'transparent',
        className: 'gantt-no-dates',
        ...(parent !== undefined ? { parent } : {}),
        ...(parent ? { dependency: [parent] } : {}),
        custom: r,
      } as GanttPointData;
    });

    // Build left-side grid columns.
    // columns[0] configures the main treegrid axis — NO labels.format so Highcharts uses its own
    // mapOfPosToGridNode formatter (which resolves node names correctly from the tree structure).
    // Setting labels.format: '{point.name}' would break it because ctx.point lookup searches
    // series.options.data by p.y === tickValue, and our points have no explicit y set.
    const t = (key: string) => this.translate.instant(`gantt.${key}`);

    const containerWidth = this.getEffectiveContainerWidth();
    const totalWeight = Math.max(
      1,
      fields.reduce((sum, f) => sum + (f.width ?? 1), 0),
    );
    const colBudget = Math.max(
      0,
      Math.floor(containerWidth * GANTT_GRID_WIDTH_RATIO) - fields.length * GANTT_COLUMN_PADDING_PX,
    );
    const colWidth = (f: DataListFields) =>
      Math.min(
        GANTT_MAX_PIXELS_PER_WEIGHT * (f.width ?? 1),
        Math.max(1, Math.floor(((f.width ?? 1) / totalWeight) * colBudget)),
      );

    const gridColumns: Highcharts.XAxisOptions[] = [
      ...fields.map((field, index) => {
        const fieldKey = this.tableDataService.getFieldName(field);
        const w = colWidth(field);
        const col: Highcharts.XAxisOptions = {
          title: {
            text: field?.label ?? String(field.id),
            style: {
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              color: 'black',
              width: w,
            },
          },
          labels: { useHTML: true, style: { width: w } },
        };
        if (index === 0) {
          col.labels = {
            useHTML: true,
            style: { width: w },
            formatter: (context: any) => {
              const axis = context.axis;
              const name = axis?.mapOfPosToGridNode?.[context.pos]?.name ?? String(context.value);
              return this.ganttService.formatTruncatedGridCell(name, w);
            },
          };
        } else {
          col.labels = {
            useHTML: true,
            style: { width: w },
            formatter: (context: any) => {
              const point = context.point;
              const value = point?.custom?.[fieldKey];
              let fullText: string;
              if (value == null || value === '') {
                fullText = field.typeId === FieldTypeEnum.Date ? '—' : '';
              } else if (field.typeId === FieldTypeEnum.Date) {
                fullText = datePipe.transform(value, field.restrictions) ?? '';
              } else if (field.typeId === FieldTypeEnum.Number) {
                fullText = numberPipe.transform(value, field.formattings, field.restrictions, currency) ?? '';
              } else {
                fullText = String(value);
              }
              return this.ganttService.formatTruncatedGridCell(fullText, w);
            },
          };
        }
        return col;
      }),
    ];

    const twoWeeks = 14 * oneDay;
    const xRange = this.ganttService.computeXAxisRange(ganttData, now, oneDay, twoWeeks);
    const xMin = Number.isFinite(xRange.min) ? xRange.min : now - twoWeeks;
    const xMax = Number.isFinite(xRange.max) ? xRange.max : now + twoWeeks;
    const maxVisibleRows = this.getMaxVisibleRows();
    const chartHeight = Math.max(
      GANTT_MIN_CHART_HEIGHT_PX,
      this.containerHeight() - (config?.dependencyFieldId ? GANTT_DEPENDENCY_BANNER_HEIGHT_PX : 0),
    );
    // Reentrancy guard for the render-time auto-fit below.
    let yAxisFitting = false;
    return {
      chart: {
        height: chartHeight,
        events: {
          load: function (this: Highcharts.Chart) {
            const chart = this;
            applyGanttYAxisCollapseScrollMode(chart);

            /* Scroll to top will re-render the chart labels, and truncate them.
               Deferred to after firstRender so plotHeight is available, avoiding NaN transforms. */
            const maxVisible = chart.yAxis[0].options.max as number | undefined;
            if (maxVisible != null && Number.isFinite(maxVisible) && chart.plotHeight > 0) {
              setTimeout(() => {
                if (chart.yAxis?.[0] && chart.plotHeight > 0) {
                  chart.yAxis[0].setExtremes(0, maxVisible, true, false);
                }
              }, 0);
            }
            chart.container.addEventListener(
              'wheel',
              (e: WheelEvent) => {
                e.preventDefault();
                if (e.shiftKey) {
                  const xAxis = chart.xAxis[0];
                  const { min, max, dataMin, dataMax } = xAxis.getExtremes();
                  const range = (max as number) - (min as number);
                  const shift = range * 0.1 * (e.deltaY > 0 ? 1 : -1);
                  const scrollMin = dataMin as number;
                  const scrollMax = dataMax as number;
                  const newMin = Math.max(scrollMin, Math.min(scrollMax - range, (min as number) + shift));
                  xAxis.setExtremes(newMin, newMin + range, true, false);
                } else {
                  const yAxis = chart.yAxis[0] as GanttScrollAxis;
                  const { min, max, dataMin } = yAxis.getExtremes();
                  const range = (max as number) - (min as number);
                  // Scroll one row in PIXEL space (break/padding aware), like the native scrollbar.
                  const dir = e.deltaY > 0 ? 1 : -1;
                  const steppedMin = yAxis.toValue(yAxis.toPixels(min as number) + dir * GANTT_ROW_HEIGHT_PX);
                  // Don't scroll past the last visible row into the collapsed/empty region.
                  const maxMin = ganttMaxScrollMin(yAxis);
                  const newMin = Math.max(dataMin ?? 0, Math.min(maxMin, steppedMin));
                  // The chart render handler snaps max so the rows fill whole-row height.
                  yAxis.setExtremes(newMin, newMin + range, true, false);
                }
              },
              { passive: false },
            );
          },
          render: function (this: Highcharts.Chart) {
            applyGanttYAxisCollapseScrollMode(this);
            // Align the top to a whole row and snap max to whole rows. Rows are integer-aligned, so a
            // fractional min (left by a native scrollbar drag) renders the top row partly above the
            // plot — into the header. The wheel already steps by whole rows, so rounding leaves it
            // untouched. Done on render (not afterSetExtremes) because expand can keep min/max
            // unchanged — afterSetExtremes only fires when extremes change (Chart.redraw).
            if (yAxisFitting) {
              return;
            }
            const axis = this.yAxis[0] as GanttScrollAxis;
            if (!axis.staticScale || axis.min == null || axis.max == null || !axis.len) {
              return;
            }
            const newMin = Math.min(Math.round(axis.min), Math.floor(ganttMaxScrollMin(axis)));
            const newMax = ganttFitMax(axis, newMin);
            if (
              Math.abs(newMin - axis.min) > GANTT_EXTREMES_MIN_DELTA ||
              Math.abs(newMax - axis.max) > GANTT_EXTREMES_MAX_DELTA
            ) {
              yAxisFitting = true;
              axis.setExtremes(newMin, newMax, true, false);
              yAxisFitting = false;
            }
          },
        },
      },
      title: { text: null },
      caption: {
        text: `${t('totalRows')}: ${ganttData.length}`,
        align: 'left',
        style: { fontSize: '14px' },
      },
      time: {
        timezone: this.userStore.userData()?.ianaTimeZoneId,
      },
      connectors: {
        lineColor: '#24628f',
        marker: {
          color: '#24628f',
        },
      },
      xAxis: [
        {
          type: 'datetime',
          min: xMin,
          max: xMax,
          scrollbar: { enabled: true },
          plotLines: [
            {
              value: now,
              color: '#24628f',
              width: 2,
              zIndex: 5,
              label: { text: t('today'), align: 'center', style: { color: '#24628f', fontWeight: 'bold' } },
            },
          ],
        },
      ],
      yAxis: {
        type: 'treegrid',
        // Override Gantt default (50); axis.staticScale is set in chart events instead.
        staticScale: 0,
        minPadding: 0,
        maxPadding: 0,
        max: maxVisibleRows < ganttData.length ? maxVisibleRows : undefined,
        startOnTick: false,
        endOnTick: false,
        scrollbar: { enabled: maxVisibleRows < ganttData.length },
        grid: {
          enabled: true,
          columns: gridColumns,
        },
      },
      navigator: { enabled: true },
      plotOptions: {
        gantt: {
          /* This allows us to see the gantt bars even if they are very short */
          minPointLength: 2,
          dataLabels: [
            {
              enabled: !!labelKey,
              useHTML: true,
              formatter: labelKey
                ? function () {
                    const self = this as unknown as { point: GanttFormatterPoint; series: { chart: Highcharts.Chart } };
                    const point = self.point;
                    const value = point?.custom?.[labelKey];
                    if (value == null || value === '') return '';

                    const xAxis = self.series?.chart?.xAxis?.[0];
                    if (xAxis && point?.start != null && point?.end != null) {
                      const barWidthPx = Math.abs(xAxis.toPixels(point.end) - xAxis.toPixels(point.start));
                      if (barWidthPx < GANTT_MIN_BAR_LABEL_WIDTH_PX) return '';
                    }
                    let text: string;
                    if (descriptionField?.typeId === FieldTypeEnum.Date) {
                      text = datePipe.transform(value, descriptionField.restrictions) ?? String(value);
                    } else if (descriptionField?.typeId === FieldTypeEnum.Number) {
                      text =
                        numberPipe.transform(
                          value,
                          descriptionField.formattings,
                          descriptionField.restrictions,
                          currency,
                        ) ?? String(value);
                    } else {
                      text = String(value);
                    }
                    const textColor = point?.custom?.state_text_color;
                    return textColor ? `<span style="color:${textColor}">${text}</span>` : text;
                  }
                : undefined,
              overflow: 'justify',
              crop: true,
              allowOverlap: false,
              style: { textOutline: 'none', fontSize: '11px', textOverflow: 'ellipsis' },
            },
          ],
          point: {
            events: {
              click: (e: Highcharts.PointClickEventObject) => {
                const entity = (e.point as unknown as { custom?: GanttPointData['custom'] })?.custom;
                const currentActions = this.actions();
                if (entity && currentActions.length) {
                  const viewAction = currentActions.find(a => a.actionType === '6') ?? currentActions[0];
                  viewAction?.click?.(entity);
                }
              },
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        formatter(this: Highcharts.Point) {
          const p = this as GanttFormatterPoint;
          const isNoDate = (this as any).options?.className === 'gantt-no-dates';
          const isOngoing = (this as any).options?.className === 'gantt-ongoing';
          const isEndOnly = (this as any).options?.className === 'gantt-end-only';

          let dateInfo = '';
          if (isNoDate) {
            dateInfo = `${t('notPlanned')}`;
          } else if (isOngoing) {
            dateInfo = `${t('tooltipStart')}: ${datePipe.transform(p?.custom?.[startKey], startDateField.restrictions ?? [])}<br/>${t('tooltipEnd')}: ${t('ongoing')}`;
          } else if (isEndOnly) {
            dateInfo = `${t('tooltipStart')}: —<br/>${t('tooltipEnd')}: ${datePipe.transform(p?.custom?.[endKey], endDateField.restrictions ?? [])}`;
          } else {
            dateInfo = `${t('tooltipStart')}: ${datePipe.transform(p?.custom?.[startKey], startDateField.restrictions ?? [])}<br/>${t('tooltipEnd')}: ${datePipe.transform(p?.custom?.[endKey], endDateField.restrictions ?? [])}`;
          }

          return `
            <b>${p?.custom?.full_doc_num}</b><br/>
            ${p?.custom?.state_name ? `${t('tooltipStatus')}: ${p.custom.state_name}<br/>` : ''}
            ${dateInfo}
          `;
        },
      },
      series: [
        {
          type: 'gantt',
          name: t('documents'),
          data: ganttData as GanttPointData[],
        },
      ],
      credits: { enabled: false },
      accessibility: { enabled: false },
    };
  }

  private measureContainer(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const height = el.offsetHeight;
    const width = el.offsetWidth;

    if (Number.isFinite(height) && height > 0) {
      this.containerHeight.set(height);
    }
    if (Number.isFinite(width) && width > 0) {
      this.containerWidth.set(width);
    }
  }

  /** Width used for left-grid column budget; caps at mobile chart width on small screens. */
  private getEffectiveContainerWidth(): number {
    const measured = this.containerWidth();
    const width = Number.isFinite(measured) && measured > 0 ? measured : GANTT_DEFAULT_CONTAINER_WIDTH_PX;
    if (this.userStore.isSmallScreen()) {
      return Math.min(width, GANTT_MOBILE_CHART_WIDTH_PX);
    }
    return width;
  }

  private filterFields(fields: DataListFields[], fieldId?: number): string[] {
    return fields
      .filter(f => f.typeId === FieldTypeEnum.Date && (fieldId == null || f.id === fieldId))
      .map(f => this.tableDataService.getFieldName(f))
      .filter(Boolean);
  }

  private findDateField(fields: DataListFields[], fieldId?: number, fieldKey?: string | null): DataListFields | null {
    if (fieldId != null) {
      const byId = fields.find(f => f.id === fieldId && f.typeId === FieldTypeEnum.Date);
      if (byId) {
        return byId;
      }
    }
    if (fieldKey) {
      return (
        fields.find(f => f.typeId === FieldTypeEnum.Date && this.tableDataService.getFieldName(f) === fieldKey) ?? null
      );
    }
    return null;
  }
  private getMaxVisibleRows(): number {
    const measured = this.containerHeight();
    const containerHeight = Number.isFinite(measured) && measured > 0 ? measured : GANTT_DEFAULT_CONTAINER_HEIGHT_PX;
    // Reserve ~150px for navigator, caption, x-axis header/scrollbar
    const availableHeight = containerHeight - 150;
    return Math.max(3, Math.floor(availableHeight / GANTT_ROW_HEIGHT_PX) - 1);
  }
}
